import { useState } from 'react';
import { useCriteria } from './useCriteria';
import { useHighlights } from './useHighlights';
import { useExclusionReasons } from './useExclusionReasons';
import {
  trackReviewSetupCompleted,
  trackReviewSetupCriteriaSaved,
} from 'containers/Review/Home/reviewSetupTracking';
import { getReviewId } from 'query/review';

type UseReviewSetupCompletionResult = {
  isLoading: boolean;
  isComplete: boolean;
  completedCount: number;
  eligibilityComplete: boolean;
  highlightsComplete: boolean;
  exclusionReasonsComplete: boolean;
  markHighlightsComplete?: () => void;
  markEligibilityComplete?: () => void;
  markExclusionReasonsComplete?: () => void;
};

export const useReviewSetupCompletion = (): UseReviewSetupCompletionResult => {
  const {
    hasCriteria,
    hasFramework,
    isLoading: criteriaLoading,
  } = useCriteria();
  const { hasHighlights, isLoading: highlightsLoading } = useHighlights();
  const {
    isSortedOrModified,
    isLoading: exclusionLoading,
  } = useExclusionReasons();
  const [isCompleting, setIsCompleting] = useState(false);

  const isEligibilityComplete = !criteriaLoading && hasCriteria && hasFramework;
  const isLoading = criteriaLoading || highlightsLoading || exclusionLoading;
  const isAlreadyComplete =
    !isLoading && isEligibilityComplete && hasHighlights && isSortedOrModified;
  const completedCount = isLoading
    ? 0
    : [isEligibilityComplete, hasHighlights, isSortedOrModified].filter(Boolean)
        .length;

  const skipAllCompleteTracking =
    isLoading || isAlreadyComplete || isCompleting || completedCount !== 2;

  const markEligibilityComplete = () => {
    const reviewId = getReviewId();

    trackReviewSetupCriteriaSaved({ review_id: reviewId });

    if (skipAllCompleteTracking) return;

    if (!isEligibilityComplete) {
      setIsCompleting(true);
      trackReviewSetupCompleted({ review_id: reviewId });
    }
  };

  const markHighlightsComplete = () => {
    if (skipAllCompleteTracking) return;

    if (!hasHighlights) {
      setIsCompleting(true);
      trackReviewSetupCompleted({ review_id: getReviewId() });
    }
  };

  const markExclusionReasonsComplete = () => {
    if (skipAllCompleteTracking) return;

    if (!isSortedOrModified) {
      setIsCompleting(true);
      trackReviewSetupCompleted({ review_id: getReviewId() });
    }
  };

  return {
    isLoading,
    isComplete: isAlreadyComplete,
    completedCount,
    eligibilityComplete: isEligibilityComplete,
    highlightsComplete: hasHighlights,
    exclusionReasonsComplete: isSortedOrModified,
    markHighlightsComplete,
    markEligibilityComplete,
    markExclusionReasonsComplete,
  };
};
